import React, { useState } from 'react';
import { UserButton, useOrganizationList, useOrganization } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { Building2, X, Plus, Check, Loader2 } from 'lucide-react';
import { useCurrentUser } from './ClerkProviderWrapper';

/**
 * Fully custom org switcher panel — no Clerk OrganizationSwitcher component
 * so we have 100% control over the look and feel.
 */
function OrgSwitcherPanel({ onClose }) {
    const { userMemberships, createOrganization, isLoaded } = useOrganizationList({
        userMemberships: { infinite: true },
    });
    const { organization: activeOrg, membership } = useOrganization();

    const [view, setView] = useState('list'); // 'list' | 'create'
    const [orgName, setOrgName] = useState('');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!orgName.trim()) return;
        setCreating(true);
        setError('');
        try {
            await createOrganization({ name: orgName.trim() });
            onClose();
            window.location.href = '/';
        } catch (err) {
            setError(err?.errors?.[0]?.message || 'Failed to create organization');
        } finally {
            setCreating(false);
        }
    };

    const handleSelectOrg = async (mem) => {
        try {
            await mem.organization.setActive?.();
        } catch (_) { }
        onClose();
        window.location.href = '/';
    };

    return (
        <div className="w-full">
            {view === 'create' ? (
                /* ── Create org form ── */
                <div>
                    <div className="flex items-center gap-2 mb-5">
                        <button
                            onClick={() => setView('list')}
                            className="text-xs text-[#9ca3af] hover:text-white transition-colors"
                        >
                            ← Back
                        </button>
                        <h3 className="text-sm font-semibold text-white ml-2">New organization</h3>
                    </div>

                    <form onSubmit={handleCreate} className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-[#cccccc]">
                                Organization name
                            </label>
                            <input
                                autoFocus
                                type="text"
                                value={orgName}
                                onChange={(e) => setOrgName(e.target.value)}
                                placeholder="Organization name"
                                className="w-full px-3 py-2 rounded-md bg-[#1e1e1e] border border-[#3c3c3c] text-white text-sm placeholder-[#5a5a5a] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                            />
                            {error && (
                                <p className="text-xs text-red-400">{error}</p>
                            )}
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setView('list')}
                                className="px-3 py-1.5 text-sm rounded-md text-[#cccccc] hover:bg-[#3c3c3c] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={creating || !orgName.trim()}
                                className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-colors"
                            >
                                {creating && <Loader2 size={13} className="animate-spin" />}
                                Create
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                /* ── Org list ── */
                <div>
                    <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-3">
                        Your organizations
                    </p>

                    {!isLoaded ? (
                        <div className="flex items-center justify-center py-6 text-[#6b7280]">
                            <Loader2 size={18} className="animate-spin" />
                        </div>
                    ) : userMemberships.data?.length === 0 ? (
                        <p className="text-sm text-[#6b7280] py-2">No organizations yet.</p>
                    ) : (
                        <ul className="flex flex-col gap-1 mb-3">
                            {userMemberships.data?.map((mem) => {
                                const isActive = activeOrg?.id === mem.organization.id;
                                return (
                                    <li key={mem.organization.id}>
                                        <button
                                            onClick={() => handleSelectOrg(mem)}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive
                                                ? 'bg-[#37373d] text-white'
                                                : 'text-[#cccccc] hover:bg-[#2a2d2e] hover:text-white'
                                                }`}
                                        >
                                            {/* Org avatar */}
                                            <span className="flex-shrink-0 w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center text-xs font-bold text-white uppercase">
                                                {mem.organization.name.charAt(0)}
                                            </span>
                                            <span className="flex-1 text-left truncate">
                                                {mem.organization.name}
                                            </span>
                                            {isActive && (
                                                <Check size={14} className="text-blue-400 flex-shrink-0" />
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <div className="pt-2 border-t border-[#3c3c3c]">
                        <button
                            onClick={() => setView('create')}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#9ca3af] hover:bg-[#2a2d2e] hover:text-white transition-colors"
                        >
                            <Plus size={15} />
                            Create organization
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * User menu component that shows user info and organization switcher in dropdown.
 */
export function UserMenu() {
    const { user, isLoaded } = useCurrentUser();
    const [showOrgModal, setShowOrgModal] = useState(false);

    if (!isLoaded) {
        return (
            <div className="w-8 h-8 rounded-full bg-[#3c3c3c] animate-pulse" />
        );
    }

    return (
        <>
            <div className="flex items-center">
                <UserButton
                    afterSignOutUrl="/"
                    appearance={{
                        baseTheme: dark,
                        elements: {
                            avatarBox: 'w-8 h-8',
                            userButtonPopoverCard: 'bg-[#252526] border-[#3c3c3c]',
                            userButtonPopoverFooter: 'hidden',
                            userButtonPopoverActionButton: 'text-[#cccccc] hover:bg-[#3c3c3c]',
                            userButtonPopoverActionButtonIcon: 'text-[#cccccc]',
                        },
                    }}
                    userProfileProps={{
                        appearance: {
                            baseTheme: dark,
                            elements: {
                                card: 'shadow-xl border border-[#3c3c3c]',
                                navbar: 'hidden',
                                navbarMobileMenuButton: 'hidden',
                                headerTitle: 'hidden',
                                headerSubtitle: 'hidden',
                                footer: 'hidden',
                                footerAction: 'hidden',
                            }
                        }
                    }}
                >
                    <UserButton.MenuItems>
                        <UserButton.Action
                            label="Switch organization"
                            labelIcon={<Building2 size={16} />}
                            onClick={() => setShowOrgModal(true)}
                        />
                    </UserButton.MenuItems>
                </UserButton>
            </div>

            {/* Custom Org Switcher Modal */}
            {showOrgModal && (
                <>
                    <div
                        className="fixed inset-0 bg-black/60 z-50"
                        onClick={() => setShowOrgModal(false)}
                    />
                    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-[#252526] rounded-xl border border-[#3c3c3c] shadow-2xl p-5 w-[380px]">
                        <div className="flex justify-between items-center mb-5">
                            <h2 className="text-base font-semibold text-white">Organizations</h2>
                            <button
                                onClick={() => setShowOrgModal(false)}
                                className="p-1 rounded-lg hover:bg-[#3c3c3c] text-[#9ca3af] hover:text-white transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <OrgSwitcherPanel onClose={() => setShowOrgModal(false)} />
                    </div>
                </>
            )}
        </>
    );
}

export default UserMenu;
